import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import BottomSheet from '../BottomSheet';
import Icon from '../Icon';
import InstrumentBrowser from '../InstrumentBrowser';
import ModalAction from '../ModalAction';
import { DEFAULT_INSTRUMENT_ID, getInstrument, getInstrumentName } from '../../../utils/midiInstruments';
import { getSetting, setSetting, settingKeys } from '../../../utils/settings';

import { ReactComponent as ChevronIcon } from '../../../icons/arrow-right-circle-solid.svg';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';

import './InstrumentRow.scss';

/**
 * @param {boolean} [mixed] - Reads Mixed, for several parts set to different instruments
 * @param {string} [defaultId] - The instrument marked Default in the sheet
 * @param {boolean} [recallLast] - Opening the sheet on the app's default jumps to the instrument
 *   last picked anywhere. Off where opening it shouldn't make a choice, as on a song's default.
 * @param {object} [player] - From useChordBarsPreview or useMidiPreview, for the sheet's Play
 * @param {object} [browser] - Passed on to InstrumentBrowser: downloadedIds, onPreview, playingId,
 *   loadingId, progress
 * @param {function} [onOpenChange] - Told when the sheet opens and closes, for a player that should
 *   only wake while it is up
 */
const InstrumentRow = ({
  instrumentId,
  mixed = false,
  onSelect,
  defaultId,
  recallLast = true,
  player,
  browser,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const setIsOpen = (open) => {
    setOpen(open);
    onOpenChange?.(open);
  };
  const previousId = useRef(instrumentId);

  // Saved off any change while the sheet is open rather than off onSelect, so a caller whose row
  // preview also selects (the MIDI upload) is remembered too
  useEffect(() => {
    if (isOpen && instrumentId !== previousId.current) setSetting(settingKeys.lastInstrument, instrumentId);
    previousId.current = instrumentId;
  }, [isOpen, instrumentId]);

  const open = () => {
    const last = getSetting(settingKeys.lastInstrument);
    const lastId = last && getInstrument(last).id;

    // Only over the default — an instrument the part was saved with is kept
    if (recallLast && lastId && instrumentId === DEFAULT_INSTRUMENT_ID && lastId !== instrumentId) onSelect(lastId);
    setIsOpen(true);
  };

  const body = (
    <>
      <span className="label">{t('player.instrument')}</span>
      <span className="value">{mixed ? t('player.mixed') : getInstrumentName(getInstrument(instrumentId))}</span>
      {onSelect && <Icon icon={<ChevronIcon />} className="solid chevron" />}
    </>
  );

  if (!onSelect) return <div className="InstrumentRow">{body}</div>;

  const play = player && (
    <ModalAction
      icon={player.isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
      label={player.playLabel}
      active={player.isPlaying}
      busy={player.playBusy}
      progress={player.playProgress}
      disabled={!player.canPlay}
      onClick={player.isPlaying ? player.stop : player.play}
    />
  );

  return (
    <>
      <button type="button" className="InstrumentRow" onClick={open}>
        {body}
      </button>

      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('player.instrument')}
        className="InstrumentSheet"
        action={play}
      >
        <InstrumentBrowser value={instrumentId} onSelect={onSelect} defaultId={defaultId} {...browser} />
      </BottomSheet>
    </>
  );
};

InstrumentRow.propTypes = {
  instrumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Makes the row a picker. Omitted where the instrument is not the viewer's to change. */
  onSelect: PropTypes.func,
  mixed: PropTypes.bool,
  defaultId: PropTypes.string,
  recallLast: PropTypes.bool,
  player: PropTypes.shape({
    isPlaying: PropTypes.bool,
    play: PropTypes.func,
    stop: PropTypes.func,
    canPlay: PropTypes.bool,
    playLabel: PropTypes.string,
    playBusy: PropTypes.bool,
    playProgress: PropTypes.number,
  }),
  browser: PropTypes.object,
  onOpenChange: PropTypes.func,
};

export default InstrumentRow;
